import { useState, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import { getPublicMediaUrl } from '../lib/mediaHelpers';
import { getRelatedSongs, type SongForConnections } from '../lib/exploreConnectionsService';
import { supabase } from '../lib/supabaseClient';

interface ExploreConnectionsPreviewProps {
  songId: string;
  disabled?: boolean;
  onChange?: (songIds: string[]) => void;
  savedSongIds?: string[];
}

interface PreviewSong {
  id: string;
  title: string;
  artist: string;
  image_url: string | null;
  primary_tags: string[];
  explore_caption?: string;
  caption?: string;
}

export default function ExploreConnectionsPreview({
  songId,
  disabled = false,
  onChange,
  savedSongIds = [],
}: ExploreConnectionsPreviewProps) {
  const [loading, setLoading] = useState(false);
  const [previewSongs, setPreviewSongs] = useState<PreviewSong[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Track if the current operation is from a user action (shuffle/recompute)
  const isUserActionRef = useRef(false);
  const lastSavedIdsRef = useRef<string | null>(null);

  useEffect(() => {
    if (songId) {
      const currentSavedIds = savedSongIds.join(',');

      // If this is from a user action, skip reloading and just update the ref
      if (isUserActionRef.current) {
        isUserActionRef.current = false;
        lastSavedIdsRef.current = currentSavedIds;
        return;
      }

      // Only reload if the saved IDs actually changed (or if this is the first load)
      if (currentSavedIds !== lastSavedIdsRef.current || lastSavedIdsRef.current === null) {
        lastSavedIdsRef.current = currentSavedIds;

        if (savedSongIds.length > 0) {
          loadSavedSongs(savedSongIds);
        } else {
          // IMPORTANT: Notify parent on initial compute so the state is populated for saving
          loadPreview(true);
        }
      }
    }
  }, [songId, savedSongIds.join(',')]);

  async function loadSavedSongs(songIds: string[]) {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('songs')
        .select('id, title, artist, image_url, primary_tags, tags, description')
        .in('id', songIds);

      console.log('[ExploreConnectionsPreview] Fetch result:', { data, error: fetchError });

      if (fetchError) {
        console.error('[ExploreConnectionsPreview] Database error:', fetchError);
        throw fetchError;
      }

      if (data && data.length > 0) {
        console.log('[ExploreConnectionsPreview] Found songs:', data.length);

        // Preserve the order from savedSongIds
        const orderedSongs = songIds
          .map(id => data.find(s => s.id === id))
          .filter((s): s is NonNullable<typeof s> => s !== undefined);

        const preview: PreviewSong[] = orderedSongs.map(song => ({
          id: song.id,
          title: song.title,
          artist: song.artist,
          image_url: song.image_url || null,
          primary_tags: song.primary_tags || song.tags || [],
          caption: song.description,
        }));

        console.log('[ExploreConnectionsPreview] Preview songs:', preview);
        setPreviewSongs(preview);
      } else {
        console.warn('[ExploreConnectionsPreview] No songs found, falling back to recommendations');
        // If no saved songs found, fall back to recommendations (don't notify parent to avoid infinite loop)
        await loadPreview(false);
      }
    } catch (err) {
      console.error('[ExploreConnectionsPreview] Error loading saved songs:', err);
      console.error('[ExploreConnectionsPreview] Error details:', JSON.stringify(err));

      // Fall back to computing recommendations on error (don't notify parent to avoid infinite loop)
      console.log('[ExploreConnectionsPreview] Falling back to computed recommendations');
      await loadPreview(false);
    } finally {
      setLoading(false);
    }
  }

  async function loadPreview(notifyParent: boolean = true) {
    setLoading(true);
    setError(null);
    try {
      const connections = await getRelatedSongs({ id: songId } as SongForConnections);

      const preview: PreviewSong[] = connections.map(song => ({
        id: song.id,
        title: song.title,
        artist: song.artist,
        image_url: song.image_url || null,
        primary_tags: song.primary_tags || song.tags || [],
        explore_caption: song.explore_caption,
        caption: song.caption,
      }));

      setPreviewSongs(preview);

      // Notify parent of the new song IDs only if explicitly requested
      if (notifyParent && onChange) {
        // Mark as user action so useEffect doesn't reload
        isUserActionRef.current = true;
        onChange(preview.map(s => s.id));
      }
    } catch (err) {
      console.error('[ExploreConnectionsPreview] Error:', err);
      setError('Failed to compute preview');
      setPreviewSongs([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-[13px] font-medium text-[#A0A0A0] mb-3">
          Auto-Generated Connections (Top 3)
        </h4>

        {loading ? (
          <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-8 text-center">
            <p className="text-[13px] text-[#606060]">Computing recommendations...</p>
          </div>
        ) : error ? (
          <div className="bg-[#FF5555]/10 border border-[#FF5555]/30 rounded-lg p-4 text-center">
            <p className="text-[13px] text-[#FF5555]">{error}</p>
          </div>
        ) : previewSongs.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-8 text-center">
            <p className="text-[13px] text-[#606060]">No related songs available yet.</p>
            <p className="text-[11px] text-[#505050] mt-2">
              Make sure this song has tags and other published songs exist.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {previewSongs.map((song, index) => (
              <div
                key={song.id}
                className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-3"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <img
                        src={getPublicMediaUrl(song.image_url)}
                        alt={song.title}
                        className="w-12 h-12 rounded object-cover"
                        onError={e => {
                          (e.target as HTMLImageElement).src = '/placeholder-civicbeat.svg';
                        }}
                      />
                      <div className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-[#40E0D0] rounded-full flex items-center justify-center">
                        <span className="text-[10px] font-bold text-[#0A0A0A]">
                          {index + 1}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-[#F5F5F5] truncate">
                      {song.title}
                    </p>
                    <p className="text-[12px] text-[#808080] truncate mt-0.5">
                      {song.artist}
                    </p>

                    {(song.explore_caption || song.caption) && (
                      <p className="text-[11px] text-[#A0A0A0] mt-2 line-clamp-2">
                        {song.explore_caption || song.caption}
                      </p>
                    )}

                    {song.primary_tags && song.primary_tags.length > 0 && (
                      <div className="flex items-center gap-1 mt-2 flex-wrap">
                        {song.primary_tags.slice(0, 3).map(tag => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-[#181818] rounded text-[10px] text-[#A0A0A0]"
                          >
                            {tag}
                          </span>
                        ))}
                        {song.primary_tags.length > 3 && (
                          <span className="text-[10px] text-[#606060]">
                            +{song.primary_tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
